import { useEffect } from "react"
import { AlertTriangle, Building2, Lock, type LucideIcon } from "lucide-react"
import {
  useLocation,
  useNavigate,
  useSearchParams,
  type NavigateFunction,
} from "react-router-dom"

import { Button } from "@/components/ui/button"
import {
  hasMultipleOrgs,
  initiateOrgSwitch,
  orgSwitchLabel,
  orgSwitchReturnsToGlobalOrders,
} from "../../services/org-data"
import {
  flushAccessDenialDiagToLogRocket,
  recordAccessDenialDiag,
} from "../../util/access-denial-log"
import logo from "../../asset/images/logo.png"

export type UnauthorizedReason = "permission" | "entity-access" | "unknown"

export interface UnauthorizedLocationState {
  reason?: UnauthorizedReason
}

const REASONS: readonly string[] = ["permission", "entity-access", "unknown"]

const isReason = (value: string | null): value is UnauthorizedReason =>
  !!value && REASONS.includes(value)

interface VariantConfig {
  icon: LucideIcon
  title: string
  body: string
  primary: { label: string; onClick: () => void }
  secondary?: { label: string; onClick: () => void }
}

const goHome = () => window.location.assign("/")
const reload = () => window.location.reload()

function resolveVariant(
  reason: UnauthorizedReason,
  navigate: NavigateFunction,
): VariantConfig {
  switch (reason) {
    case "entity-access": {
      // Multi-org users (INTERNAL_ADMIN, multi-entity CUSTOMER_ADMIN) get a
      // Switch-organization CTA. Single-org users (typical CUSTOMER_USER)
      // can't switch to anything — show only Go-to-home and adjust the copy
      // so we're not implying there's another org they could use.
      if (hasMultipleOrgs()) {
        // The sentence names the same destination as the button label, so a
        // post-processing user isn't told to switch organizations by a button
        // that says Back to Global Orders.
        const switchSentence = orgSwitchReturnsToGlobalOrders()
          ? "Go back to Global Orders or return home."
          : "Switch organizations or return home."
        return {
          icon: Building2,
          title: "Resource Not Available",
          body: `The link you followed points to a resource that isn't in any of your organizations. ${switchSentence}`,
          primary: {
            label: orgSwitchLabel("Switch Organization"),
            onClick: () => navigate(initiateOrgSwitch()),
          },
          secondary: { label: "Go to Home", onClick: goHome },
        }
      }
      return {
        icon: Building2,
        title: "Resource Not Available",
        body:
          "The link you followed points to a resource your account doesn't have access to. Return home to continue.",
        primary: { label: "Go to Home", onClick: goHome },
      }
    }
    case "unknown":
      return {
        icon: AlertTriangle,
        title: "Something Went Wrong",
        body:
          "We hit an unexpected problem. Try again, or return home and pick up from there.",
        primary: { label: "Try Again", onClick: reload },
        secondary: { label: "Go to Home", onClick: goHome },
      }
    case "permission":
    default:
      return {
        icon: Lock,
        title: "Access Denied",
        body:
          "You don't have permission to view this page. If you believe this is an error, please contact your administrator.",
        primary: { label: "Go to Home", onClick: goHome },
      }
  }
}

/**
 * Access-denied page explaining permission/entity-access reasons, with org-switch escape hatch when the user belongs to multiple orgs.
 * The reason is read from a `?reason=permission|entity-access|unknown` query
 * param, falling back to router state, then to `permission`. The escape
 * hatch's label follows its destination ("Back to Global Orders" for
 * post-processing users).
 *
 * @category Pages
 */
function Unauthorized() {
  const location = useLocation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  // Query string first: the auth bootstrap has no router to write state into,
  // and a param survives the full-page trip that state does not (D16).
  // Router state stays supported for the in-app navigate('/error', { state })
  // callers, which are unchanged.
  const fromQuery = searchParams.get("reason")
  const reason: UnauthorizedReason = isReason(fromQuery)
    ? fromQuery
    : (location.state as UnauthorizedLocationState | null)?.reason ?? "permission"
  const variant = resolveVariant(reason, navigate)
  const Icon = variant.icon

  // Access-denial telemetry (DV-3012 §6.3). NOT temporary: 27 of the 28 prod
  // denial sessions in the 30 days to 2026-09-09 came from the deep-link
  // resolution path, and they were still occurring. `hadState` used to live
  // here to detect a reason lost across a navigation — impossible now that the
  // reason travels in the URL — so only the rendered variant is recorded.
  //
  // Retire this, and the recordAccessDenialDiag calls in
  // entity-scope-resolution.ts, once BC-8 has shipped and the
  // resolveEntityScope-sourced event count has sat at rest for a full release
  // cycle.
  useEffect(() => {
    recordAccessDenialDiag("lastErrorPageRender", "access-denied-page-render", {
      renderedReason: reason,
    })
    // By the time /error renders, LogRocket.init has run (in a prior commit or
    // the prior document), so flush the snapshot(s) now. No-ops if not ready.
    flushAccessDenialDiagToLogRocket()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <section className="flex min-h-screen flex-col items-center justify-center bg-muted p-6 text-center">
      <div className="w-full max-w-[480px] rounded-lg border border-border bg-background px-10 py-12 shadow-modal">
        <div className="flex flex-col items-center gap-1">
          <div className="mb-8">
            <img
              src={logo}
              alt="PlanOmatic logo"
              width="184"
              height="43"
            />
          </div>
          <div className="mx-auto mb-6 flex h-14 w-14 items-center justify-center rounded-full bg-secondary">
            <Icon className="h-7 w-7 text-muted-foreground" strokeWidth={1.5} />
          </div>
          <div className="mb-3">
            <h1 className="text-2xl font-medium text-foreground">
              {variant.title}
            </h1>
          </div>
          <div className="mb-8 leading-relaxed text-muted-foreground">
            <span className="text-base">{variant.body}</span>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-2">
            {variant.secondary && (
              <Button
                variant="outline"
                onClick={variant.secondary.onClick}
              >
                {variant.secondary.label}
              </Button>
            )}
            <Button onClick={variant.primary.onClick}>
              {variant.primary.label}
            </Button>
          </div>
        </div>
      </div>
    </section>
  )
}

export default Unauthorized
